class ScrollRestoration {
  constructor() {
    this.scrollPositions = new Map();
    this.navigationState = new Map();
    this.MAX_STORED_POSITIONS = 50; // Limit stored positions to prevent memory issues
    this.cleanupOldPositions();
  }

  cleanupOldPositions() {
    // Clean up old scroll positions from sessionStorage on initialization
    try {
      const now = Date.now();
      const maxAge = 30 * 60 * 1000; // 30 minutes
      const keysToRemove = [];
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('scroll_')) {
          try {
            const stored = sessionStorage.getItem(key);
            if (stored) {
              const data = JSON.parse(stored);
              // Remove if older than 30 minutes
              if (data.timestamp && (now - data.timestamp > maxAge)) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // If we can't parse it, it's old format or corrupted - remove it
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Silent fail
        }
      });
    } catch (e) {
      // Silent fail - sessionStorage might be unavailable
    }
  }

  saveScrollPosition(locationKey, scrollY = window.scrollY) {
    if (locationKey && scrollY > 0) { // Only save if we've actually scrolled
      const viewportWidth = window.innerWidth;
      const scrollData = {
        position: scrollY,
        viewport: viewportWidth,
        timestamp: Date.now(),
        documentHeight: document.body.scrollHeight // Save document height for validation
      };
      
      this.scrollPositions.set(locationKey, scrollData);
      try {
        sessionStorage.setItem(`scroll_${locationKey}`, JSON.stringify(scrollData));
      } catch (e) {
        // Fallback to simple format if JSON fails
        try {
          sessionStorage.setItem(`scroll_${locationKey}`, String(scrollY));
        } catch (e2) {
        }
      }
    }
  }

  getScrollPosition(locationKey) {
    if (!locationKey) return null;
    
    let scrollData = this.scrollPositions.get(locationKey);
    
    if (scrollData === undefined) {
      try {
        const stored = sessionStorage.getItem(`scroll_${locationKey}`);
        if (stored) {
          try {
            // Try parsing as JSON first (new format)
            scrollData = JSON.parse(stored);
          } catch (e) {
            // Fallback to old format (plain number)
            const position = parseInt(stored, 10);
            if (!isNaN(position) && position > 0) {
              scrollData = { position, viewport: window.innerWidth };
            } else {
              scrollData = null;
            }
          }
        } else {
          scrollData = null;
        }
      } catch (e) {
        // Silent fail in production
        scrollData = null;
      }
    }
    
    // If we have scroll data, check if viewport hasn't changed too much
    if (scrollData && typeof scrollData === 'object') {
      const currentViewport = window.innerWidth;
      const savedViewport = scrollData.viewport || currentViewport;
      
      // Define breakpoints
      const getBreakpoint = (width) => {
        if (width < 640) return 'mobile';
        if (width < 768) return 'sm-tablet';
        if (width < 1024) return 'tablet';
        if (width < 1280) return 'desktop';
        return 'large';
      };
      
      const currentBreakpoint = getBreakpoint(currentViewport);
      const savedBreakpoint = getBreakpoint(savedViewport);
      
      // If breakpoint changed, return null to invalidate restoration
      if (currentBreakpoint !== savedBreakpoint) {
        return null;
      }
      
      // Validate position is a reasonable number
      const position = scrollData.position;
      if (typeof position === 'number' && !isNaN(position) && position >= 0) {
        return position;
      }
    }
    
    // Old format or number - return as is with validation
    if (typeof scrollData === 'number' && !isNaN(scrollData) && scrollData >= 0) {
      return scrollData;
    }
    
    return null;
  }

  restoreScrollPosition(locationKey, callback) {
    const position = this.getScrollPosition(locationKey);
    
    if (position !== null && position !== undefined && position > 0) {
      // Check if we're on a tablet-like device (between 640px and 1024px)
      const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;
      
      const performScroll = () => {
        try {
          // Verify we can actually scroll to this position
          const maxScroll = Math.max(
            document.body.scrollHeight || 0,
            document.body.offsetHeight || 0,
            document.documentElement.clientHeight || 0,
            document.documentElement.scrollHeight || 0,
            document.documentElement.offsetHeight || 0
          ) - window.innerHeight;
          
          // Clamp position to valid scroll range (ensure it's never negative)
          const targetPosition = Math.max(0, Math.min(position, maxScroll));
          
          if (targetPosition > 0) {
            // Use scrollTo with object notation for better browser compatibility
            window.scrollTo({
              top: targetPosition,
              left: 0,
              behavior: 'auto' // Use 'auto' for instant scroll, not 'smooth'
            });
            
            // Verify scroll actually happened (with timeout to allow scroll to complete)
            setTimeout(() => {
              try {
                const actualPosition = window.scrollY || window.pageYOffset || 0;
                const scrolledClose = Math.abs(actualPosition - targetPosition) < 100;
                
                if (scrolledClose && callback) {
                  callback();
                }
              } catch (e) {
                // Silent fail - call callback anyway
                if (callback) callback();
              }
            }, 100);
            
            return true;
          }
        } catch (e) {
          // Fallback to simple scroll if modern API fails
          try {
            window.scrollTo(0, position);
            if (callback) callback();
            return true;
          } catch (e2) {
            // Silent fail in production
            return false;
          }
        }
        
        return false;
      };
      
      // On tablets, add a delay to ensure layout is fully stable
      // Tablets need more time for content reflow after navigation
      if (isTablet) {
        // Use a combination of requestAnimationFrame and setTimeout for reliable restoration
        try {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Additional small timeout to ensure DOM is fully rendered
              setTimeout(() => {
                performScroll();
              }, 50);
            });
          });
        } catch (e) {
          // Fallback if RAF fails
          setTimeout(() => performScroll(), 150);
        }
        return true; // Return true to indicate restoration was initiated
      } else {
        // On mobile and desktop, minimal delay is sufficient
        try {
          requestAnimationFrame(() => {
            performScroll();
          });
        } catch (e) {
          // Fallback if RAF fails
          performScroll();
        }
        return true; // Return true to indicate restoration was initiated
      }
    }
    
    return false;
  }

  clearScrollPosition(locationKey) {
    if (locationKey) {
      this.scrollPositions.delete(locationKey);
      try {
        sessionStorage.removeItem(`scroll_${locationKey}`);
      } catch (e) {
      }
    }
  }

  clearAll() {
    this.scrollPositions.clear();
    this.navigationState.clear();
    
    try {
      // Get all keys at once to avoid issues during iteration
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('scroll_') || key === 'lastListView')) {
          keys.push(key);
        }
      }
      
      // Remove all scroll-related keys
      keys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Silent fail for individual items
        }
      });
    } catch (e) {
      // Silent fail in production - sessionStorage might be unavailable
    }
  }

  setNavigatingToDetail(fromKey) {
    if (fromKey) {
      this.navigationState.set('lastListView', fromKey);
      try {
        sessionStorage.setItem('lastListView', fromKey);
      } catch (e) {
      }
    }
  }

  getLastListView() {
    let lastView = this.navigationState.get('lastListView');
    
    if (!lastView) {
      try {
        lastView = sessionStorage.getItem('lastListView');
      } catch (e) {
      }
    }
    
    return lastView;
  }

  clearNavigationState() {
    this.navigationState.clear();
    try {
      sessionStorage.removeItem('lastListView');
    } catch (e) {
    }
  }
}

const scrollRestoration = new ScrollRestoration();

export default scrollRestoration;